import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowRight, Building2, Box, MapPin, CreditCard, MessageCircle, Rocket } from "lucide-react";

const STEPS = [
  { id: "company", label: "Dados da empresa", icon: Building2, desc: "Confirme nome, telefone e localização" },
  { id: "unit", label: "Primeira unidade", icon: MapPin, desc: "Configure endereço e dados da unidade" },
  { id: "plans", label: "Planos e preços", icon: Box, desc: "Defina os planos que você oferece" },
  { id: "spaces", label: "Espaços", icon: MapPin, desc: "Cadastre salas e estações disponíveis" },
  { id: "payments", label: "Pagamentos", icon: CreditCard, desc: "Configure Asaas (PIX, boleto, cartão)" },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, desc: "Conecte seu WhatsApp para automação" },
  { id: "go_live", label: "Go Live", icon: Rocket, desc: "Tudo pronto, comece a operar!" },
];

export default function Onboarding() {
  const [tenant, setTenant] = useState(null);
  const [completed, setCompleted] = useState({});
  const [current, setCurrent] = useState(0);
  const nav = useNavigate();

  const load = () => api.get("/saas/me").then(({ data }) => {
    setTenant(data.tenant);
    setCompleted(data.tenant?.onboarding || {});
    // Auto-select first incomplete step
    const firstPending = STEPS.findIndex(s => !data.tenant?.onboarding?.[s.id]);
    setCurrent(firstPending === -1 ? STEPS.length - 1 : firstPending);
  });

  useEffect(() => { load(); }, []);

  const markDone = async (step, done = true) => {
    try {
      await api.patch("/saas/onboarding", { step, done });
      await load();
      if (done) toast.success("Passo concluído!");
    } catch { toast.error("Erro ao salvar"); }
  };

  const allDone = STEPS.every(s => completed[s.id]);
  const completedCount = STEPS.filter(s => completed[s.id]).length;
  const progress = (completedCount / STEPS.length) * 100;

  if (!tenant) return <div className="min-h-screen flex items-center justify-center text-gray-500">Carregando…</div>;

  const stepData = STEPS[current];

  return (
    <div className="min-h-screen bg-[#F3F4F6] p-4 sm:p-6 lg:p-10 admin-content-area" data-testid="onboarding-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Onboarding</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#14213D] font-light">Vamos configurar seu coworking, {tenant.owner_name?.split(" ")[0]}!</h1>
          <p className="text-gray-500 mt-2">Siga os passos abaixo. Você pode pular e voltar quando quiser.</p>

          <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Progresso</span>
              <span className="text-xs font-semibold text-[#14213D]">{completedCount}/{STEPS.length}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Steps list */}
          <aside className="lg:col-span-4 bg-white rounded-lg border border-gray-200 p-3 h-fit">
            {STEPS.map((s, idx) => {
              const done = !!completed[s.id];
              const active = idx === current;
              return (
                <button
                  key={s.id}
                  onClick={() => setCurrent(idx)}
                  className={`w-full text-left flex items-start gap-3 p-3 rounded-md transition ${active ? "bg-[#14213D] text-white" : "hover:bg-gray-50"}`}
                  data-testid={`onb-step-${s.id}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {done ? <CheckCircle2 size={18} className={active ? "text-[#D4AF37]" : "text-emerald-500"} /> : <Circle size={18} className={active ? "text-white/50" : "text-gray-300"} />}
                  </div>
                  <div className="flex-1">
                    <div className={`text-xs uppercase tracking-wider font-bold ${active ? "text-[#D4AF37]" : "text-gray-400"}`}>{`Passo ${idx + 1}`}</div>
                    <div className={`text-sm font-semibold ${active ? "text-white" : "text-[#14213D]"}`}>{s.label}</div>
                  </div>
                </button>
              );
            })}
          </aside>

          {/* Current step */}
          <main className="lg:col-span-8 bg-white rounded-lg border border-gray-200 p-4 sm:p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
                <stepData.icon size={20} className="text-[#D4AF37]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Passo {current + 1} de {STEPS.length}</div>
                <h2 className="font-display text-2xl text-[#14213D]">{stepData.label}</h2>
              </div>
            </div>
            <p className="text-gray-600 mb-6">{stepData.desc}</p>

            {/* Step-specific content */}
            {current === 0 && (
              <div className="space-y-3 bg-gray-50 border border-gray-200 rounded p-4 text-sm">
                <div><strong>Empresa:</strong> {tenant.company_name}</div>
                <div><strong>Responsável:</strong> {tenant.owner_name}</div>
                <div><strong>E-mail:</strong> {tenant.owner_email}</div>
                <div><strong>Telefone:</strong> {tenant.phone || "—"}</div>
                <a href="/admin/configuracoes" className="inline-flex items-center gap-1 text-[#D4AF37] text-xs hover:underline">Editar dados →</a>
              </div>
            )}
            {current === 1 && (
              <div className="text-sm space-y-3">
                <p>Sua primeira unidade já foi criada automaticamente. Personalize endereço e detalhes:</p>
                <a href="/admin/unidades" className="inline-flex items-center gap-2 bg-[#14213D] text-white px-4 py-2 rounded text-sm font-semibold">Configurar unidade <ArrowRight size={14} /></a>
              </div>
            )}
            {current === 2 && (
              <div className="text-sm space-y-3">
                <p>Já criamos 2 planos modelo para você. Ajuste preços e descrições:</p>
                <a href="/admin/planos" className="inline-flex items-center gap-2 bg-[#14213D] text-white px-4 py-2 rounded text-sm font-semibold">Configurar planos <ArrowRight size={14} /></a>
              </div>
            )}
            {current === 3 && (
              <div className="text-sm space-y-3">
                <p>Cadastre as salas, mesas e estações do seu coworking:</p>
                <a href="/admin/espacos" className="inline-flex items-center gap-2 bg-[#14213D] text-white px-4 py-2 rounded text-sm font-semibold">Cadastrar espaços <ArrowRight size={14} /></a>
              </div>
            )}
            {current === 4 && (
              <div className="text-sm space-y-3">
                <p>Configure sua chave Asaas para começar a cobrar PIX, boleto e cartão automaticamente:</p>
                <a href="/admin/configuracoes" className="inline-flex items-center gap-2 bg-[#14213D] text-white px-4 py-2 rounded text-sm font-semibold">Configurar Asaas <ArrowRight size={14} /></a>
              </div>
            )}
            {current === 5 && (
              <div className="text-sm space-y-3">
                <p>Conecte seu WhatsApp para enviar confirmações de reserva e cobranças automaticamente:</p>
                <a href="/admin/configuracoes" className="inline-flex items-center gap-2 bg-[#14213D] text-white px-4 py-2 rounded text-sm font-semibold">Configurar WhatsApp <ArrowRight size={14} /></a>
              </div>
            )}
            {current === 6 && (
              <div className="text-sm space-y-3">
                {allDone || completed.go_live ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
                    <div className="font-display text-lg text-emerald-900 mb-1">🎉 Tudo pronto!</div>
                    <p className="text-emerald-800">Seu coworking está configurado. Acesse o painel administrativo para começar a operar.</p>
                    <button onClick={() => nav("/admin")} className="mt-3 bg-[#14213D] text-white px-4 py-2 rounded text-sm font-semibold inline-flex items-center gap-2">Ir para o painel <ArrowRight size={14} /></button>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded p-4">
                    <div className="font-semibold text-amber-900 mb-1">Quase lá!</div>
                    <p className="text-amber-800">Finalize os passos pendentes para ir ao ar.</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                onClick={() => setCurrent(Math.max(0, current - 1))}
                disabled={current === 0}
                className="text-sm text-gray-500 hover:text-[#14213D] disabled:opacity-30 self-start"
              >← Anterior</button>
              <div className="flex items-center gap-3 flex-wrap">
                {!completed[stepData.id] && (
                  <button
                    onClick={() => markDone(stepData.id, true)}
                    className="bg-[#D4AF37] text-[#14213D] px-5 py-2 rounded text-sm font-bold"
                    data-testid={`onb-done-${stepData.id}`}
                  >Marcar como concluído</button>
                )}
                <button
                  onClick={() => setCurrent(Math.min(STEPS.length - 1, current + 1))}
                  disabled={current === STEPS.length - 1}
                  className="bg-[#14213D] text-white px-5 py-2 rounded text-sm font-semibold disabled:opacity-30 inline-flex items-center gap-1"
                >Próximo <ArrowRight size={14} /></button>
              </div>
            </div>
          </main>
        </div>

        <div className="text-center mt-6">
          <button onClick={() => nav("/admin")} className="text-sm text-gray-500 hover:text-[#14213D]" data-testid="skip-onboarding">
            Pular onboarding e ir para o painel →
          </button>
        </div>
      </div>
    </div>
  );
}
